import React, { useState, useEffect } from "react";
import { Country, State, City } from "country-state-city";
import { useCurrency } from "../utility/CurrencyContext";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import { toast } from "react-toastify";
import PersonalInfoForm from "../form/PersonalInfoForm";

const Form = ({ cart, calculateItemPrice, convertPrice, onSubmit, setShippingAddress }) => {
  const { currency, unit } = useCurrency();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    town: "",
    country: "",
    state: "",
    postalCode: "",
    notes: "",
    unit: unit,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get all countries for dropdown
  const countries = Country.getAllCountries();

  // Get states based on selected country
  const getStates = () => {
    if (!formData.country) return [];
    return State.getStatesOfCountry(formData.country);
  };

  // Get cities based on selected state and country
  const getCities = () => {
    if (!formData.country || !formData.state) return [];
    return City.getCitiesOfState(formData.country, formData.state);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear state and city when country changes
    if (name === "country") {
      setFormData((prev) => ({
        ...prev,
        state: "",
        town: "",
      }));
    }

    // Clear city when state changes
    if (name === "state") {
      setFormData((prev) => ({
        ...prev,
        town: "",
      }));
    }
  };

  useEffect(() => {
    if (formData.country || formData.state || formData.town) {
      // Get full names for country and state
      const countryName = Country.getCountryByCode(formData.country)?.name || "";
      const stateName = State.getStateByCodeAndCountry(formData.state, formData.country)?.name || "";

      const shippingAddress = {
        country: countryName,
        state: stateName,
        town: formData.town,
        pin_code: formData.postalCode,
      };

      setShippingAddress(shippingAddress);
      localStorage.setItem("shippingAddress", JSON.stringify(shippingAddress));
    }
  }, [formData.country, formData.state, formData.town, formData.postalCode, setShippingAddress]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.phone) newErrors.phone = "Phone is required";
    if (!formData.address) newErrors.address = "Address is required";
    if (!formData.country) newErrors.country = "Country is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.town) newErrors.town = "City is required";
    if (!formData.postalCode) newErrors.postalCode = "Postal code is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // useRazorpayScript(); //load razorpay script
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const countryData = Country.getCountryByCode(formData.country);
      const stateData = State.getStateByCodeAndCountry(
        formData.state,
        formData.country
      );

      const shippingAddress = {
        ...formData,
        country: countryData.name,
        state: stateData?.name || formData.state,
        countryCode: formData.country,
        stateCode: formData.state,
        currency: currency,
      };

      // 1) Subtotal using the shared 5-tier calculateItemPrice
      const subtotal = cart.reduce((total, item) => {
        const unitPrice = calculateItemPrice(item.quantity, item);
        return total + unitPrice * item.quantity;
      }, 0);

      const vat = subtotal * 0.18;
      const totalAmount = Number(subtotal + vat).toFixed(2);

      let amountInINR;
      if (currency === "EURO") {
        amountInINR = totalAmount * 101.74;
      } else if (currency === "DOLLAR") {
        amountInINR = totalAmount * 86.53;
      } else {
        amountInINR = totalAmount;
      }
      const amountInPaise = Number(amountInINR).toFixed(2);

      // 2) itemsToOrder: send the correct tier-resolved unit price
      const itemsToOrder = cart.map((item) => {
        const resolvedUnitPrice = calculateItemPrice(item.quantity, item);

        let unitPriceInINR = resolvedUnitPrice;
        if (currency === "EURO") {
          unitPriceInINR = resolvedUnitPrice * 101.74;
        } else if (currency === "DOLLAR") {
          unitPriceInINR = resolvedUnitPrice * 86.53;
        }

        return {
          product_id: item.id,
          name: item.name,
          unit_price: Number(unitPriceInINR.toFixed(2)),
          quantity: item.quantity,
        };
      });

      const payload = {
        personalInfo: { ...shippingAddress, amount: amountInPaise },
        cartItems: itemsToOrder,
      };

      const response = await fetch(
        `${config.productURL}/payment/instamojo_checkout`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      if (response.ok && data && data.longurl) {
        window.location.href = data.longurl;
      } else {
        throw new Error(data.error || "Failed to initiate payment");
      }
    } catch (err) {
      console.error("Order Error:", err);
      setErrors({
        form: err.message || "An error occurred during payment processing",
      });
      toast.error(err.message || "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <PersonalInfoForm
            formData={formData}
            errors={errors}
            handleChange={handleChange}
          />

          {/* Shipping Address */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Shipping Address</h2>

            <div>
              <label
                htmlFor="address"
                className="block text-sm font-medium text-gray-700"
              >
                Street Address *
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="country"
                className="block text-sm font-medium text-gray-700"
              >
                Country *
              </label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country.isoCode} value={country.isoCode}>
                    {country.name}
                  </option>
                ))}
              </select>
              {errors.country && (
                <p className="mt-1 text-sm text-red-600">{errors.country}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="state"
                className="block text-sm font-medium text-gray-700"
              >
                State/Province *
              </label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                disabled={!formData.country}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
              >
                <option value="">Select State</option>
                {getStates().map((state) => (
                  <option key={state.isoCode} value={state.isoCode}>
                    {state.name}
                  </option>
                ))}
              </select>
              {errors.state && (
                <p className="mt-1 text-sm text-red-600">{errors.state}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="town"
                  className="block text-sm font-medium text-gray-700"
                >
                  City *
                </label>
                <select
                  id="town"
                  name="town"
                  value={formData.town}
                  onChange={handleChange}
                  disabled={!formData.state}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                >
                  <option value="">Select City</option>
                  {getCities().map((city) => (
                    <option key={city.name} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {errors.town && (
                  <p className="mt-1 text-sm text-red-600">{errors.town}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="postalCode"
                  className="block text-sm font-medium text-gray-700"
                >
                  Postal Code *
                </label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
                {errors.postalCode && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.postalCode}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Order Notes */}
        <div>
          <label
            htmlFor="notes"
            className="block text-sm font-medium text-gray-700"
          >
            Order Notes (Optional)
          </label>
          <textarea
            id="notes"
            name="notes"
            rows={3}
            value={formData.notes}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-indigo-600 text-white py-2 px-6 rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Processing..." : "Place Order"}
          </button>
        </div>

        {errors.form && (
          <div className="text-red-600 text-sm mt-2">{errors.form}</div>
        )}
      </form>
    </div>
  );
};

export default Form;